import { useState, useEffect, useRef } from "react";
import { X, Download, Share } from "lucide-react";
import { Button } from "./ui/button";
import { useI18n } from "../lib/i18n";

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;

const InstallBanner = ({ variant = "default" }) => {
  const { t } = useI18n();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  const storageKey = `pwa-install-dismissed-${variant}`;

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(storageKey)) return;

    if (isIOS()) {
      setShowBanner(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // For browsers that support install but haven't fired the event yet, show after delay
    const timer = setTimeout(() => {
      if (!isIOS() && !deferredPrompt) setShowBanner(true);
    }, 3000);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      clearTimeout(timer);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === "accepted") {
        setShowBanner(false);
        localStorage.setItem(storageKey, "installed");
      }
      setDeferredPrompt(null);
    } else if (isIOS()) {
      setShowIOSGuide(true);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem(storageKey, "dismissed");
  };

  const bannerRef = useRef(null);
  useEffect(() => {
    document.body.classList.toggle("has-install-banner", showBanner);
    if (!showBanner) { document.documentElement.style.removeProperty("--install-banner-h"); return; }
    const el = bannerRef.current;
    const measure = () => document.documentElement.style.setProperty("--install-banner-h", `${el?.offsetHeight || 0}px`);
    measure();
    const ro = el && typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => { ro?.disconnect(); document.body.classList.remove("has-install-banner"); document.documentElement.style.removeProperty("--install-banner-h"); };
  }, [showBanner]);

  if (!showBanner) return null;

  return (
    <>
      <div ref={bannerRef} className="sticky bottom-0 inset-x-0 z-40 mt-auto border-t border-border bg-card/95 backdrop-blur px-3 py-2 animate-fade-in" style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }} data-testid="install-banner">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Download className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium leading-tight truncate">{t("install.title", "Installer Kintsugi TCC")}</p>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">{t("install.subtitle", "Accès rapide depuis votre écran d'accueil")}</p>
          </div>
          <Button size="sm" onClick={handleInstall} data-testid="install-btn" className="shrink-0 text-xs gap-1.5">
            {isIOS() ? t("install.howTo", "Comment faire") : t("install.install", "Installer")}
          </Button>
          <button onClick={handleDismiss} className="p-1 rounded-md hover:bg-muted transition shrink-0" aria-label="Fermer" data-testid="install-dismiss">
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* iOS Guide Modal */}
      {showIOSGuide && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setShowIOSGuide(false)}>
          <div className="bg-card rounded-2xl shadow-2xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="ios-guide-modal">
            <h3 className="font-heading text-lg font-semibold mb-4">{t("install.screenTitle", "Installer sur votre écran")}</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold shrink-0">1</div>
                <p className="text-sm text-muted-foreground">{t("install.step1", "Appuyez sur le bouton")} <Share className="w-4 h-4 inline text-primary" /> <strong>{t("install.share", "Partager")}</strong> {t("install.step1b", "en bas de Safari")}</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold shrink-0">2</div>
                <p className="text-sm text-muted-foreground">{t("install.step2", "Faites défiler et appuyez sur")} <strong>{t("install.addToHome", "Sur l'écran d'accueil")}</strong></p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold shrink-0">3</div>
                <p className="text-sm text-muted-foreground">{t("install.step3", "Appuyez sur")} <strong>{t("install.add", "Ajouter")}</strong> {t("install.step3b", "en haut à droite")}</p>
              </div>
            </div>
            <Button onClick={() => setShowIOSGuide(false)} className="w-full mt-6" data-testid="ios-guide-close">
              {t("install.understood", "Compris")}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallBanner;
